import { AxiosError } from 'axios';
import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import useAuthUtilities from '../AuthMiddleware/AuthUtilities';
import territoriesJson from '../assets/territories.json';
import { QueryParams } from '@/clients/BaseAPIClient';
import { productApiClient } from '@/clients/ProductAPIClient';
import { trackApiClient } from '@/clients/TrackApiClient';
import { masterAPIClient } from '@/clients/MasterAPIClient';
import { RestrictionSet } from '@/components/partials/Restrictions/RestrictionSet';
import { IAlbum, IAsset } from '@/data/models/album';
import { AlbumState } from '@/data/models/albumState';
import { Format } from '@/data/models/format';
import { ILabelDetail } from '@/data/models/label';
import { StateHistory } from '@/data/models/stateHistory';
import { IAlbumSalesTeamPrice } from '@/data/models/salesTerritory';
import { RestrictionSchemeType } from '@/data/models/territory';
import { ITrack } from '@/data/models/track';
import {
  ErrorCode,
  ErrorType,
  IValidationResults,
  ValidationLevel,
} from '@/data/models/validation';
import { getLoadAlbumState } from '@/selectors/album';
import { getAuth } from '@/selectors/auth';
import { ILoadAlbum, loadAlbum } from '@/state/actions/albumAction';
import store from '../state/store';
import { logger } from '@/utils/Logger';
import {
  getValidationIssuesFromPayload,
  isValidationError,
  isValidationWarning,
} from '@/utils/validationErrors';
import useGetProjectQuery from '@/hooks/useGetProjectQuery';
import useRemoveFromProjectMutation from '@/hooks/useRemoveFromProjectMutation';
import { PricingData } from './usePricingModal';
import { IMultipleTrackEdit } from '@/types/multiple-track-types';
import {
  getProductViewMode,
  ProductViewModes,
} from '@/utils/productStateUtils';
import { IChangeRequest } from '@/data/models/changeRequest';
import { ICurrentChanges } from '@/types/product-current-changes-types';
import { ProductViewHook } from '@/types/product-view-model';

/** Album payload returned by route-specific data loaders. */
export interface ProductViewLoadResult {
  album?: IAlbum;
  albumId?: string;
  validationChangeRequestId?: string | number | null;
}

/** Result returned after saving when the view should reload a specific album ID. */
export interface ProductViewSaveResult {
  reloadAlbumId?: string;
}

/** Context passed to custom track reloaders, mainly used by Change Request mode. */
export interface ReloadTracksContext {
  album: IAlbum;
  albumId: string;
}

/**
 * Adapter contract between route-specific hooks and the shared view hook.
 *
 * To add a new product view flow, keep UI behavior here and provide only the
 * loading, saving, and optional track-reload behavior from the route hook.
 */
export interface UseProductViewCommonProps {
  productViewMode: ProductViewModes;
  loadAlbumData: (
    albumId: string,
    changeRequestId?: string
  ) => Promise<ProductViewLoadResult | undefined>;
  saveAlbumData: (
    updatedAlbum: IAlbum
  ) => Promise<ProductViewSaveResult | void>;
  reloadTracksData?: (
    context: ReloadTracksContext
  ) => Promise<IAlbum | undefined>;
  changeRequest?: IChangeRequest;
  currentChanges?: ICurrentChanges;
}

/**
 * Builds the complete view model consumed by product and merchandise templates.
 */
export const useProductViewCommon = ({
  productViewMode,
  loadAlbumData,
  saveAlbumData,
  reloadTracksData,
  changeRequest,
  currentChanges,
}: UseProductViewCommonProps): ProductViewHook => {
  const { t } = useTranslation();
  const { id: albumId, crId: changeRequestId } = useParams<{
    id: string;
    crId?: string;
  }>();
  const navigate = useNavigate();
  const [album, setAlbum] = useState<IAlbum>();
  const [labelInfo, setLabelInfo] = useState<ILabelDetail>();
  const [parentLabelInfo, setParentLabelInfo] = useState<ILabelDetail>();
  const [restrictions, setRestrictions] = useState<RestrictionSet>();
  const [isLoading, setIsLoading] = useState(false);
  const [errorFlag, setErrorFlag] = useState(false);
  const [errorBanner, setErrorBanner] = useState(false);
  const [errors, setErrors] = useState<IValidationResults[]>([]);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [warningMessages, setWarningMessages] = useState<string[]>([]);
  const [integrationErrorMessages, setIntegrationErrorMessages] = useState<
    string[]
  >([]);
  const [currentStateHistory, setCurrentStateHistory] =
    useState<StateHistory>();
  const [readonly, setReadonly] = useState(true);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [
    showRemoveFromProjectConfirmationModal,
    setShowRemoveFromProjectConfirmationModal,
  ] = useState(false);
  const { isJustCreated } = useSelector(getLoadAlbumState);
  const { hasWriteAccess } = useSelector(getAuth);
  const [track, setTrack] = useState<ITrack>({
    id: 0,
    albumId: '',
    trackNumber: 0,
    isrc: '',
    displayArtist: '',
    title: '',
    version: '',
    discOrSideNumber: 1,
  });
  const [multipleTracksEdit, setMultipleTracksEdit] =
    useState<IMultipleTrackEdit>();

  const { canUserEditProduct } = useAuthUtilities();
  const [editCount, setEditCount] = useState(0);
  const { data: project, refetch: refetchProject } = useGetProjectQuery();
  const removeFromProject = useRemoveFromProjectMutation();

  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [showEditTrackModal, setShowEditTrackModal] = useState(false);
  const [showEditMultipleTracksModal, setShowEditMultipleTracksModal] =
    useState(false);
  const [showRestrictionsModal, setShowRestrictionsModal] = useState(false);
  const [showLabelInfoModal, setShowLabelInfoModal] = useState(false);
  const [showParentLabelInfoModal, setShowParentLabelInfoModal] =
    useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);

  const togglePricingModal = () => {
    setShowPricingModal(!showPricingModal);
  };

  useEffect(() => {
    if (!albumId) return;

    if (changeRequestId) {
      loadData(albumId, changeRequestId);
    } else {
      loadData(albumId);
    }
  }, [albumId, changeRequestId, productViewMode]);

  useEffect(() => {
    errorMessagehandling(errorFlag);
  }, [errorFlag, errorBanner, integrationErrorMessages, errors]);

  const removeProject = () => {
    if (!project?.id || !albumId) {
      return;
    }

    removeFromProject.mutate(
      {
        projectId: project?.id,
        contentUrn: albumId,
      },
      {
        onSuccess: () => {
          refetchProject();
        },
      }
    );
  };

  const buildProductValidationMessages = (
    validationResults: IValidationResults[]
  ) => {
    let messages: string[] = [];

    //differentiate the list of errors depending the error code and type
    const metadataConflictErrors: IValidationResults[] =
      validationResults.filter(
        (va) => va.errorType === ErrorType.MetadataConflict
      );
    if (metadataConflictErrors.length > 0)
      messages.push(
        t('validationAlert.productEditView.isrcMetadataConflictErrorMessage')
      );

    const productInfoMissingOrInvalidErrors: IValidationResults[] =
      validationResults.filter(
        (va) =>
          va.errorCode === ErrorCode.ProductInfoMissingOrInvalid ||
          (va.errorCode === ErrorCode.ArtistList &&
            va.errorType === ErrorType.EmptyList &&
            va.validationLevel === ValidationLevel.Album)
      );
    if (productInfoMissingOrInvalidErrors.length > 0)
      messages.push(
        t(
          'validationAlert.productEditView.productInfoMissingOrInvalidErrorMessage'
        )
      );

    const productInfoArtistSeqeunceErrors: IValidationResults[] =
      validationResults.filter(
        (va) =>
          va.errorCode === ErrorCode.ArtistList &&
          va.errorType === ErrorType.SequenceError &&
          va.validationLevel === ValidationLevel.Album
      );
    if (productInfoArtistSeqeunceErrors.length > 0)
      messages.push(
        t(
          'validationAlert.productEditView.productInfoArtistSeqeunceErrorMessage'
        )
      );
    const productCoverArtInvalid: IValidationResults[] =
      validationResults.filter(
        (va) => va.errorCode === ErrorCode.InvalidCoverArt
      );
    if (productCoverArtInvalid.length > 0)
      messages.push(
        t('validationAlert.productEditView.productInfoInvalidCoverArt')
      );

    const productInfoNoTracksErrors: IValidationResults[] =
      validationResults.filter(
        (va) =>
          va.errorCode === ErrorCode.TrackList &&
          va.errorType === ErrorType.EmptyList
      );
    if (productInfoNoTracksErrors.length > 0)
      messages.push(
        t('validationAlert.productEditView.productInfoNoTracksErrorMessage')
      );

    const productInfoIncorrectVolumeOrTrackNumErrors: IValidationResults[] =
      validationResults.filter(
        (va) =>
          va.errorCode === ErrorCode.TrackList &&
          va.errorType === ErrorType.IncorrectVolumeOrTrackNumber
      );
    if (productInfoIncorrectVolumeOrTrackNumErrors.length > 0)
      messages.push(
        t(
          'validationAlert.productEditView.productInfoIncorrectVolumeOrTrackNumErrorMessage'
        )
      );

    const productInfoIncorrectVolumeSeqErrors: IValidationResults[] =
      validationResults.filter(
        (va) =>
          va.errorCode === ErrorCode.TrackList &&
          va.errorType === ErrorType.IncorrectVolumeSequence
      );
    if (productInfoIncorrectVolumeSeqErrors.length > 0)
      messages.push(
        t(
          'validationAlert.productEditView.productInfoIncorrectVolumeSeqErrorMessage'
        )
      );

    const productInfoIncorrectTrackNumSeqErrors: IValidationResults[] =
      validationResults.filter(
        (va) =>
          va.errorCode === ErrorCode.TrackList &&
          va.errorType === ErrorType.IncorrectTrackNumberSequence
      );
    if (productInfoIncorrectTrackNumSeqErrors.length > 0)
      messages.push(
        t(
          'validationAlert.productEditView.productInfoIncorrectTrackNumSeqErrorMessage'
        )
      );

    const trackInfoMissingOrInvalidErrors: IValidationResults[] =
      validationResults.filter(
        (va) =>
          (va.errorCode === ErrorCode.TrackInfoMissingOrInvalid &&
            va.errorType !== ErrorType.MetadataConflict) ||
          (va.errorCode === ErrorCode.ArtistList &&
            va.errorType === ErrorType.EmptyList &&
            va.validationLevel === ValidationLevel.Track) ||
          (va.errorCode === ErrorCode.WriterComposerList &&
            va.errorType === ErrorType.EmptyList &&
            va.validationLevel === ValidationLevel.Track) ||
          (va.errorCode === ErrorCode.PublisherList &&
            va.errorType === ErrorType.EmptyList &&
            va.validationLevel === ValidationLevel.Track)
      );
    if (trackInfoMissingOrInvalidErrors.length > 0)
      messages.push(
        t(
          'validationAlert.productEditView.trackInfoMissingOrInvalidErrorMessage'
        )
      );

    const trackInfoArtistSeqeunceErrors: IValidationResults[] =
      validationResults.filter(
        (va) =>
          va.errorCode === ErrorCode.ArtistList &&
          va.errorType === ErrorType.SequenceError &&
          va.validationLevel === ValidationLevel.Track
      );
    if (trackInfoArtistSeqeunceErrors.length > 0)
      messages.push(
        t('validationAlert.productEditView.trackInfoArtistSeqeunceErrorMessage')
      );

    const trackInfoWriterComposerSeqeunceErrors: IValidationResults[] =
      validationResults.filter(
        (va) =>
          va.errorCode === ErrorCode.WriterComposerList &&
          va.errorType === ErrorType.SequenceError &&
          va.validationLevel === ValidationLevel.Track
      );
    if (trackInfoWriterComposerSeqeunceErrors.length > 0)
      messages.push(
        t(
          'validationAlert.productEditView.trackInfoWriterComposerSeqeunceErrorMessage'
        )
      );

    const trackInfoPublisherSeqeunceErrors: IValidationResults[] =
      validationResults.filter(
        (va) =>
          va.errorCode === ErrorCode.PublisherList &&
          va.errorType === ErrorType.SequenceError &&
          va.validationLevel === ValidationLevel.Track
      );
    if (trackInfoPublisherSeqeunceErrors.length > 0)
      messages.push(
        t(
          'validationAlert.productEditView.trackInfoPublisherSeqeunceErrorMessage'
        )
      );

    const restrictionsErrors: IValidationResults[] = validationResults.filter(
      (va) => va.validationLevel === ValidationLevel.Restrictions
    );
    if (restrictionsErrors.length > 0)
      messages = messages.concat(
        restrictionsErrors.map((e) =>
          t(
            `validationAlert.restrictions.${e.fieldName}.${e.errorType}.ErrorMessage`
          )
        )
      );

    return messages;
  };

  const errorMessagehandling = (errorFlag: boolean) => {
    let newErrorMessages: string[] = [];
    let newWarningMessages: string[] = [];
    if (
      (errorFlag === true || errorBanner === true) &&
      errors.length !== 0 &&
      errors !== null
    ) {
      newErrorMessages = buildProductValidationMessages(
        errors.filter(isValidationError)
      );
      newWarningMessages = buildProductValidationMessages(
        errors.filter(isValidationWarning)
      );
    }

    if (integrationErrorMessages && integrationErrorMessages.length > 0) {
      newErrorMessages = newErrorMessages.concat(integrationErrorMessages);
    }

    setErrorMessages(newErrorMessages);
    setWarningMessages(newWarningMessages);
  };

  const setProductViewMode = (albumData: IAlbum) => {
    if (productViewMode === ProductViewModes.Edit) {
      const userHasEditPermission = canUserEditProduct(albumData.albumState);
      const resolvedProductViewMode = getProductViewMode(
        albumData.albumState,
        userHasEditPermission
      );
      setReadonly(resolvedProductViewMode !== ProductViewModes.Edit);
      setDisabled(false);
    }
    if (productViewMode === ProductViewModes.ReadOnly) {
      setReadonly(true);
      setDisabled(false);
    }
    if (productViewMode === ProductViewModes.Disabled) {
      setReadonly(false);
      setDisabled(true);
    }
    if (productViewMode === ProductViewModes.ChangeRequest) {
      setReadonly(false);
      setDisabled(false);
    }
    if (productViewMode === ProductViewModes.ChangeRequestReadOnly) {
      setReadonly(true);
      setDisabled(false);
    }
  };

  const loadData = async (id: string, crId?: string) => {
    setAlbum(undefined);
    setIsLoading(true);

    try {
      // Route hooks decide where the album comes from; this hook only loads the shared view state.
      const result = await loadAlbumData(id, crId);
      if (!result) {
        setIsLoading(false);
        navigate('/404?product');
        return;
      }
      await loadCommonData(
        result?.album,
        result?.albumId ?? id,
        result?.validationChangeRequestId
      );
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        navigate('/404?product');
      }
    }
  };

  const loadCommonData = async (
    albumData?: IAlbum,
    albumId?: string,
    validationChangeRequestId?: string | number | null
  ) => {
    if (!albumData) {
      return;
    }

    setAlbum(albumData);
    setIsLoading(false);
    setProductViewMode(albumData);

    if (isJustCreated === undefined) {
      const createAlbumValues: ILoadAlbum = {
        isJustCreated: false,
      };
      await store.dispatch(loadAlbum(createAlbumValues));
    }

    validateAlbum(albumData, validationChangeRequestId);

    const territoryRestrictionSet = new RestrictionSet(
      territoriesJson,
      albumData
    );
    setRestrictions(territoryRestrictionSet);

    if (albumData.albumMetaData?.stateHistory) {
      const currentState = [...albumData.albumMetaData.stateHistory].sort(
        (x, y) => (x.timestamp > y.timestamp ? -1 : 1)
      )[0];
      setCurrentStateHistory(currentState);
    }

    if (albumData.albumState !== AlbumState.Error) {
      setIntegrationErrorMessages([]);
      return;
    }

    try {
      const integrationErrorsAlbumId = albumData.id ?? albumId;

      if (!integrationErrorsAlbumId) {
        return;
      }

      const intErrors = await masterAPIClient.getIntegrationErrors(
        integrationErrorsAlbumId
      );

      setIntegrationErrorMessages(
        intErrors && intErrors.length > 0
          ? intErrors.map((e: any) => e.errorMessage)
          : [
              t(
                'product.integrationErrorFallback',
                'Failed to retrieve integration errors.'
              ),
            ]
      );
      setErrorBanner(true);
    } catch (error) {
      setIntegrationErrorMessages([
        t(
          'product.integrationErrorFallback',
          'Failed to retrieve integration errors.'
        ),
      ]);
      setErrorBanner(true);
    }
  };

  const validateAlbum = (
    albumData: any,
    validationChangeRequestId?: string | number | null
  ) => {
    setErrors([]);
    setErrorFlag(false);
    setErrorBanner(false);
    const loadAlbumStore: ILoadAlbum = store.getState().loadAlbumState;
    if (
      productViewMode !== ProductViewModes.ReadOnly &&
      productViewMode !== ProductViewModes.ChangeRequestReadOnly &&
      loadAlbumStore.isJustCreated === false &&
      hasWriteAccess
    ) {
      const albumJson = JSON.stringify(albumData);
      const changeRequestValidationId =
        validationChangeRequestId ??
        changeRequest?.id ??
        albumData.activeChangeRequestId;
      productApiClient
        .validateAlbum(albumJson, false, changeRequestValidationId)
        .then((response: any) => {
          const validationIssues = getValidationIssuesFromPayload(response);
          if (validationIssues.length !== 0) {
            setErrors(validationIssues);
            setErrorFlag(true);
          }
        })
        .catch((error: any) => {
          logger.error(error);
        });
    }
  };

  const toggleEditProductModal = () => {
    setShowEditProductModal(!showEditProductModal);
  };
  const toggleEditTrackModal = (trackDetail: ITrack) => {
    setShowEditTrackModal(!showEditTrackModal);
    setTrack(trackDetail);
  };
  const toggleEditMultipleTracksModal = (
    trackList?: ITrack[],
    areNewTracks = false
  ) => {
    if (!trackList) {
      setShowEditMultipleTracksModal(false);
      setMultipleTracksEdit(undefined);
      return;
    }

    if (!album || trackList.length === 0) return;

    setShowEditMultipleTracksModal(true);
    setMultipleTracksEdit({
      album: album,
      trackList: trackList,
      areNewTracks,
    });
    setTrack(trackList[0]);
  };
  const toggleRestrictionsModal = () => {
    setShowRestrictionsModal(!showRestrictionsModal);
  };
  const toggleLabelInfoModal = async () => {
    if (!album?.label.adminOrgId) return;
    const response = await productApiClient.getLabelDetailById(
      album.label.adminOrgId
    );
    setLabelInfo(response.data);
    setShowLabelInfoModal(!showLabelInfoModal);
  };
  const toggleParentLabelInfoModal = async () => {
    if (!album?.label.parentAdminOrgId) return;
    const response = await productApiClient.getLabelDetailById(
      album.label.parentAdminOrgId
    );
    setParentLabelInfo(response.data);
    setShowParentLabelInfoModal(!showParentLabelInfoModal);
  };

  const reloadProductTracksData = async (album: IAlbum): Promise<IAlbum> => {
    if (!albumId) return album;

    const albumData: IAlbum = album;
    const state: QueryParams = { albumId: albumId };
    let newRowData = await trackApiClient.getTracks(state);
    newRowData = newRowData.data.map((item: ITrack) => item);
    albumData.tracks = newRowData;
    return albumData;
  };

  const ReloadTracks = async () => {
    if (!album || !albumId) return;

    // Change Request mode reloads tracks by reloading the CR album; product mode uses the track endpoint.
    const updatedAlbum = reloadTracksData
      ? await reloadTracksData({ album, albumId })
      : await reloadProductTracksData(album);

    if (!updatedAlbum) return;

    setAlbum(updatedAlbum);
    validateAlbum(updatedAlbum);
  };

  const updateProductRestrictions = async (
    restrictions: string[],
    restrictionScheme: RestrictionSchemeType
  ) => {
    if (!album) return;
    const updatedAlbum = cloneDeep(album);
    updatedAlbum.albumMetaData!.territoryRestrictions = restrictions;
    updatedAlbum.albumMetaData!.territoryRestrictionScheme =
      RestrictionSchemeType[restrictionScheme];
    await updateProduct(updatedAlbum, true);
  };

  const updateProductPricing = async (
    albumId: number,
    pricing: PricingData[]
  ) => {
    if (!album) return;
    const updatedAlbum = cloneDeep(album);

    updatedAlbum.albumSalesTeams = pricing.map(
      (p) =>
        ({
          salesTeamId: p.salesTeamId,
          price: p.price,
          sequence: p.sequence,
          isPrimary: p.isPrimary,
          isCustomPrice: p.isCustomPrice,
        }) as IAlbumSalesTeamPrice
    );

    await updateProduct(updatedAlbum, true);
  };

  const updateProductCoverArt = async (asset: IAsset | null) => {
    if (!album) return;
    const updatedAlbum = cloneDeep(album);

    updatedAlbum.coverArt = asset;
    await updateProduct(updatedAlbum, true);
  };

  const updateProduct = async (updatedAlbum: IAlbum, throwOnError = false) => {
    if (!updatedAlbum.id) return;

    try {
      // Saving is delegated to the route data source so this function stays mode-agnostic.
      const result = await saveAlbumData(updatedAlbum);
      await loadData(result?.reloadAlbumId ?? updatedAlbum.id);
    } catch (error: any) {
      logger.error(error);
      if (throwOnError) {
        throw error;
      }
    }
  };

  const UpdateProductTracks = async (tracks: ITrack[]) => {
    if (!album) return;

    const updatedAlbum = cloneDeep(album);
    updatedAlbum.tracks = tracks;
    await updateProduct(updatedAlbum);
  };

  const loadPreviousTrack = () => {
    if (!album) return;
    const previousTrackOnCurrentVolume = album.tracks.find(
      (t) =>
        t.trackNumber === track.trackNumber - 1 &&
        t.discOrSideNumber === track.discOrSideNumber
    );
    if (previousTrackOnCurrentVolume) {
      setTrack(previousTrackOnCurrentVolume);
      return;
    }

    const previousVolume = getPreviousVolume();
    if (previousVolume?.length) {
      const lastTrackOnPreviousVolume = previousVolume?.reduce((curr, prev) =>
        curr.trackNumber > prev.trackNumber ? curr : prev
      );
      setTrack(lastTrackOnPreviousVolume);
    }
  };

  const getPreviousVolume = () => {
    if (!album) return;
    const volumesWithTracks = album.tracks
      .map((t) => t.discOrSideNumber)
      .filter((value, index, array) => array.indexOf(value) === index);
    const volumesBeforeCurrentTrack = volumesWithTracks.filter(
      (v) => v < track.discOrSideNumber
    );
    const previousVolume = volumesBeforeCurrentTrack.length
      ? Math.max(...volumesBeforeCurrentTrack)
      : Math.max(...volumesWithTracks);
    return album.tracks.filter((t) => t.discOrSideNumber === previousVolume);
  };

  const loadNextTrack = () => {
    if (!album) return;
    const nextTrackOnCurrentVolume = album.tracks.find(
      (t) =>
        t.trackNumber === track.trackNumber + 1 &&
        t.discOrSideNumber === track.discOrSideNumber
    );
    if (nextTrackOnCurrentVolume) {
      setTrack(nextTrackOnCurrentVolume);
      return;
    }

    const nextVolume = getNextVolume();
    const firstTrackOnNextVolume = nextVolume?.find((t) => t.trackNumber === 1);
    if (firstTrackOnNextVolume) {
      setTrack(firstTrackOnNextVolume);
    }
  };

  const getNextVolume = () => {
    if (!album) return;
    const volumesWithTracks = album.tracks
      .map((t) => t.discOrSideNumber)
      .filter((value, index, array) => array.indexOf(value) === index);
    const volumesAfterCurrentTrack = volumesWithTracks.filter(
      (v) => v > track.discOrSideNumber
    );
    const nextVolume = volumesAfterCurrentTrack.length
      ? Math.min(...volumesAfterCurrentTrack)
      : Math.min(...volumesWithTracks);
    return album.tracks.filter((t) => t.discOrSideNumber === nextVolume);
  };

  const usesSides = (formatName: string | null): boolean => {
    const sideTypes = [
      Format.Cassette.toString(),
      Format.Vinyl7Inch.toString(),
      Format.Vinyl10Inch.toString(),
      Format.Vinyl12Inch.toString(),
    ];
    return sideTypes.includes(formatName ?? '');
  };

  const onProductUpdated = () => {
    if (!albumId) return;

    loadData(albumId);
    setEditCount(editCount + 1);
  };

  const showValidationErrors = () => {
    if (productViewMode === ProductViewModes.ChangeRequest) {
      return true;
    }

    if (
      album?.albumState === AlbumState.PendingImport ||
      album?.albumState === AlbumState.Released
    ) {
      return editCount > 0;
    }

    return true;
  };

  return {
    album,
    labelInfo,
    parentLabelInfo,
    restrictions,
    isLoading,
    errorFlag,
    errorBanner,
    errors,
    errorMessages,
    warningMessages,
    currentStateHistory,
    readonly,
    disabled,
    showRemoveFromProjectConfirmationModal,
    setShowRemoveFromProjectConfirmationModal,
    track,
    multipleTracksEdit,
    project,
    editCount,
    showEditProductModal,
    showEditTrackModal,
    showEditMultipleTracksModal,
    showRestrictionsModal,
    showLabelInfoModal,
    showParentLabelInfoModal,
    removeProject,
    toggleEditProductModal,
    toggleEditTrackModal,
    toggleEditMultipleTracksModal,
    toggleRestrictionsModal,
    toggleLabelInfoModal,
    toggleParentLabelInfoModal,
    ReloadTracks,
    updateProductRestrictions,
    UpdateProductTracks,
    loadPreviousTrack,
    loadNextTrack,
    onProductUpdated,
    showValidationErrors,
    setErrorBanner,
    setErrors,
    refetchProject,
    usesSides,
    showPricingModal,
    togglePricingModal,
    updateProductPricing,
    changeRequest,
    currentChanges,
    updateProductCoverArt,
  };
};
